import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { useLocation, useNavigationType } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { defer } from 'lodash';

import { useTrackEvent } from '@/lib/analytics';
import { useSelectedAccount, useBuildAccountUrl } from '@/lib/accounts/context';
import { generateUUID } from '@/lib/utils';
import { removeExplorationConversation, setExplorationCells } from '@/core/exploration';
import { removeConversationEditFlow } from '@/core/exploration/conversation';
import { useDirtyContext } from '@/explore/dirty-context';
import {
  buildEmptyChatCell,
  convertConversationCellsToMessages,
  discardConversationAfterMessage,
  getConversationCells,
  isChatCell,
  isConversationPart,
  isUserQuestionMessage,
  updateConversationMessage,
  isStartOfConversation,
  isEndOfConversation,
  getLastUserMessageIndex,
  removeLastChatInput,
  getConversationSelectors,
  acceptConversationAfterReply,
  isLastChatCell,
  trimTrailingNotChatCells,
  appendConversationCells,
  updateChatCellMessages,
  requiresFollowupChatCell,
} from '@/core/cell';
import {
  Exploration,
  Cell,
  ChatCell,
  ChatMessage,
  ChatExploration,
  ChatUserMessage,
  ConversationPart,
  ChatMode,
} from '@/explore/types';
import { isAbortError } from '@/lib/error/utils/abort';
import { buildExplorationHashUrl } from '@/explore/utils';
import { useAsyncCallback } from '@/lib/hooks/use-async-callback';

import { useExplorationContext } from '../exploration-context';
import { useExplorationCellContext } from '../exploration-cell-context';
import { useExplorationUIContext } from '../exploration-ui-context';
import { ChatMessages } from './components/chat-messages';
import { useChatStream } from './use-chat-stream';

const isExplorationMessage = (msg: ChatMessage | ChatExploration): msg is ChatExploration =>
  msg.type === 'exploration';

interface ChatCellViewProps {
  cell: ChatCell;
  exploration: Exploration;
}

export const ChatCellView = (props: ChatCellViewProps) => {
  const { cell, exploration: currentExploration } = props;
  const account = useSelectedAccount();
  const { setExploration, getVariables } = useExplorationContext();
  const {
    scrollToCell,
    setNewCellIndex,
    selectCell,
    selectedCell,
    conversations,
    setConversationDraft,
    setConversationProgress,
    conversationAbortCallbacks,
  } = useExplorationUIContext();
  const { cellIndex, setCell } = useExplorationCellContext();
  const trackEvent = useTrackEvent();
  const [messages, setMessages] = useState(cell.messages);
  const initialLoadDone = useRef(false);
  const streamMessages = useRef<ConversationPart[]>([]);
  const navigationType = useNavigationType();
  const location = useLocation();

  const onProgress = (message: string) => {
    setConversationProgress(cell.conversationId, message);
  };

  const onMessage = useAsyncCallback((message: ChatMessage | ChatExploration) => {
    // TTQ responded with 1 or more cells
    if (isExplorationMessage(message)) {
      appendCells(
        message.exploration.view.cells.map((c) => ({
          ...c,
          conversationId: cell.conversationId,
        })),
      );
    } else if (
      // If we've received exploration messages, append assistant message into a separate chat cell
      !isEndOfConversation(cell, currentExploration.view.cells) ||
      streamMessages.current.length > 0
    ) {
      const newCell = buildEmptyChatCell(cell.conversationId);
      newCell.messages.push(message);
      appendCells([newCell]);
    } else {
      setCell({
        ...cell,
        messages: messages.concat([message]),
      });
    }
  });

  const onError = useAsyncCallback((error) => {
    if (isAbortError(error.networkError)) {
      if (!isStartOfConversation(cell, currentExploration.view.cells) || messages.length !== 1) {
        // Revert the local message state if it wasn't the very first message attempt
        setMessages(messages.slice(0, -1));
      }
      ensureExplorationEndsWithChatCell();

      return;
    }
    generationMetadataRef.current = null;
  });

  const onComplete = useAsyncCallback(() => {
    // Clear metadata ref if generation completes (successfully or not) and wasn't already cleared
    setConversationProgress(cell.conversationId, null);
    ensureExplorationEndsWithChatCell();
    defer(trackResult);
  });

  const onTimeout = () => {
    onMessage({
      id: generateUUID(),
      role: 'assistant',
      type: 'clarifying_question',
      message: `Unfortunately, I couldn't put together a great answer in time. I could just try again, or you could help me out by rephrasing the question or breaking it down into smaller parts. How would you like me to continue?`,
    });
  };

  const { send, abort, status, statusMessage } = useChatStream({
    conversationId: cell.conversationId,
    onMessage,
    onComplete,
    onProgress,
    onError,
    onTimeout,
  });

  const generationMetadataRef = useRef<{
    startTime: number;
    searchTerm?: string;
    conversationId: string;
    correlationId: string;
    hasCurrentExploration: boolean;
    queryUrl: string;
  } | null>(null);
  const variables = getVariables();
  const { isDirtyRef, withSafeNavigation } = useDirtyContext();

  const createMessage = (question: string, sources: string[], mode: ChatMode): ChatUserMessage => {
    const lastMessageType = messages[messages.length - 1].type;
    return lastMessageType === 'clarifying_question'
      ? {
          id: generateUUID(),
          type: 'clarifying_answer',
          role: 'user',
          message: question,
        }
      : {
          id: generateUUID(),
          type: 'followup_question',
          role: 'user',
          message: question,
          sources,
          research: mode === 'research',
        };
  };

  const buildAccountUrl = useBuildAccountUrl();

  /**
   * Group frequent messages into a single exploration state update
   */
  const debouncedExplorationUpdate = useDebounceCallback(
    (cells: Cell[], cellsToAdd: ConversationPart[], currentCell: ChatCell) => {
      const updatedCells = cells.map((c) =>
        isChatCell(c) && c.id === currentCell.id ? updateChatCellMessages(cell, messages) : c,
      );
      const newExploration = setExplorationCells(
        currentExploration,
        appendConversationCells(updatedCells, cellsToAdd, currentCell.conversationId),
      );
      streamMessages.current = [];

      withSafeNavigation(() => {
        setExploration(newExploration);
        scrollToCell(cellsToAdd[0]?.id);

        if (selectedCell !== null && !isDirtyRef.current) {
          selectCell(cellsToAdd[0]?.id);
        }
      });
    },
    200,
    { trailing: true, leading: false },
  );

  const appendCells = useCallback(
    (cells: ConversationPart[]) => {
      streamMessages.current.push(...cells);
      debouncedExplorationUpdate(currentExploration.view.cells, streamMessages.current, cell);
    },
    [cell, currentExploration.view.cells, debouncedExplorationUpdate],
  );

  // Add an empty chat cell for user follow-ups if conversation doesn't already end with a ChatCell
  const ensureExplorationEndsWithChatCell = useCallback(() => {
    const requiresFollowup = requiresFollowupChatCell(
      currentExploration.view.cells,
      cell.conversationId,
      cell.id,
      streamMessages.current,
    );
    requiresFollowup && appendCells([buildEmptyChatCell(cell.conversationId)]);
  }, [cell.conversationId, cell.id, currentExploration.view.cells, appendCells]);

  const isLastCellInConversation = useMemo(
    () => isEndOfConversation(cell, currentExploration.view.cells),

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentExploration.view.cells.length],
  );
  /**
   * Discards incomplete tail end of a conversation, where we're not displaying a ChatCell as the last cell for this conversation
   */
  const removeConversationTail = useCallback(() => {
    if (isLastChatCell(currentExploration.view.cells, cell) && !isLastCellInConversation) {
      setExploration(
        setExplorationCells(
          currentExploration,
          trimTrailingNotChatCells(currentExploration.view.cells, cell.conversationId),
        ),
      );
    }
  }, [cell, currentExploration, isLastCellInConversation, setExploration]);

  const cancelRequest = useCallback(() => {
    abort();
    ensureExplorationEndsWithChatCell();
  }, [abort, ensureExplorationEndsWithChatCell]);

  const trackResult = () => {
    const metadata = generationMetadataRef.current;
    if (metadata !== null) {
      const { startTime, ...startMetadata } = metadata;
      const durationMs = Date.now() - startTime;
      const resultUrl = buildAccountUrl(buildExplorationHashUrl(currentExploration));

      trackEvent('Exploration Chat Generation Finished', {
        ...startMetadata,
        durationMs,
        resultUrl,
      });
      generationMetadataRef.current = null;
    }
  };

  const handleInput = (messages: ChatMessage[]) => {
    const correlationId = generateUUID();

    const conversationCells = getConversationCells(
      currentExploration.view.cells,
      cell.conversationId,
    );

    // Override current cell messages with those for next query
    // which can be 1) one new message added or 2) one edited and N next ones deleted
    const messagesForQuery = convertConversationCellsToMessages([
      ...conversationCells.filter(({ id }) => id !== cell.id),
      { ...cell, messages },
    ]);

    if (messagesForQuery.length === 0) {
      return Sentry.captureException(new Error('No messages to pass to chat'), {
        extra: {
          cellId: cell.id,
          conversationId: cell.conversationId,
          explorationId: currentExploration.explorationId,
        },
      });
    }

    const explorationWithoutConversations = setExplorationCells(
      currentExploration,
      currentExploration.view.cells.filter(
        (c) =>
          !isConversationPart(c) ||
          (!isChatCell(c) &&
            c.conversationId === cell.conversationId &&
            isStartOfConversation(c, currentExploration.view.cells)),
      ),
    );

    const selectors = getConversationSelectors(cell.conversationId, conversationCells);

    setConversationDraft(cell.conversationId, null);
    withSafeNavigation(() => {
      setConversationDraft(cell.conversationId, null);
    });

    const lastMessage = messagesForQuery.findLast((msg) => msg.role === 'user');
    const startMetadata = {
      searchTerm: lastMessage?.message,
      conversationId: cell.conversationId,
      hasCurrentExploration: currentExploration.view.cells.length > 1,
      correlationId,
      queryUrl: buildAccountUrl(buildExplorationHashUrl(currentExploration)),
    };
    generationMetadataRef.current = {
      ...startMetadata,
      startTime: Date.now(),
    };
    trackEvent('Exploration Chat Generation Started', startMetadata);

    send({
      accountId: account.accountId,
      exploration: explorationWithoutConversations,
      variables,
      selectors,
      messages: messagesForQuery,
    });
  };

  const handleAbort = () => {
    generationMetadataRef.current = null; // Clear metadata on abort
    cancelRequest();
  };

  const editableInitialMessage = useMemo(() => {
    const lastMessage = messages.at(-1);
    return (
      isLastCellInConversation &&
      messages.length === 1 &&
      lastMessage?.role === 'user' &&
      status === 'idle'
    );
  }, [messages, isLastCellInConversation, status]);

  useEffect(() => {
    setMessages(cell.messages);
  }, [cell.messages]);

  // Check on load if conversation is incomplete (e.g. user reloaded during a stream)
  useEffect(() => {
    if (status !== 'loading' && status !== 'streaming') {
      ensureExplorationEndsWithChatCell();
    }

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (navigationType === 'POP') {
      cancelRequest();
      removeConversationTail();
      setConversationDraft(cell.conversationId, null);
    }

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationType, location]);

  useEffect(() => {
    const callbacks = conversationAbortCallbacks.current;
    callbacks.set(cell.conversationId, cancelRequest);
    return () => {
      callbacks.delete(cell.conversationId);
    };
  }, [cell.conversationId, cancelRequest, conversationAbortCallbacks]);

  // Draft message updater
  useEffect(() => {
    // Discard has happened or user navigated back to a previous result
    if (cell.messages.length < messages.length) {
      const lastUserMessageIndex = getLastUserMessageIndex(messages);
      setConversationDraft(cell.conversationId, messages[lastUserMessageIndex].message);
    }
  }, [cell.messages, messages, cell.conversationId, setConversationDraft]);

  // Trigger initial load if necessary
  useEffect(() => {
    if (!isLastCellInConversation) {
      initialLoadDone.current = true;
      return;
    }

    const lastMessage = messages.at(-1);
    if (lastMessage?.role !== 'user' || status === 'loading') {
      initialLoadDone.current = true;
      return;
    } else if (lastMessage.type === 'initial_user_prompt' && !initialLoadDone.current) {
      initialLoadDone.current = true;
      handleInput(messages);
    }

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, status, isLastCellInConversation]);

  const pendingFirstInput = useMemo(() => {
    const cells = getConversationCells(currentExploration.view.cells, cell.conversationId);
    const chatCells = cells.filter(isChatCell);
    const lastCell = cells.at(-1);
    // User asked about a block but has not submitted or clarified yet
    const requestingEdits =
      chatCells.length === 1 && lastCell !== undefined && isChatCell(lastCell);

    return cells.every((cell) => !isChatCell(cell) || !cell.messages.length) || requestingEdits;
  }, [currentExploration.view.cells, cell.conversationId]);

  const handleSubmit = (question: string, sources: string[], mode: ChatMode) => {
    const lastMessage = messages.at(-1);
    const message = createMessage(question, sources, mode);
    const messageList = lastMessage?.role === 'user' ? messages.slice(0, -1) : messages;
    setMessages([...messageList, message]);
    handleInput([...messageList, message]);
  };

  const handleAccept = (partial = false) => {
    const conversationCells = getConversationCells(
      currentExploration.view.cells,
      cell.conversationId,
    );
    const lastFilledChatCell = (cell: ConversationPart) =>
      isChatCell(cell) && cell.messages.length > 0;
    const lastCell = conversationCells.at(-1);
    let cutoffIndex = partial ? conversationCells.findLastIndex(lastFilledChatCell) : 0;
    const hasEmptyFollowUp =
      lastCell !== undefined && isChatCell(lastCell) && lastCell.messages.length === 0;

    // If the last followup cell is filled (e.g. after "Discard last" action), we need to look for the penultimate chat cell and accept everything after that
    if (!hasEmptyFollowUp && partial) {
      const cells = conversationCells.slice(0, cutoffIndex);
      cutoffIndex = cells.findLastIndex(lastFilledChatCell);
    }

    return setExploration(
      setExplorationCells(
        currentExploration,
        acceptConversationAfterReply(cell.conversationId, currentExploration, cutoffIndex),
      ),
    );
  };

  const handleRetry = () => {
    handleInput(messages);
  };

  const handleCancel = () => {
    if (isStartOfConversation(cell, currentExploration.view.cells) || pendingFirstInput) {
      return handleDiscard();
    }

    setConversationDraft(cell.conversationId, messages.at(-2)?.message ?? null);
    setCell({ ...cell, messages: messages.slice(0, -2) });
  };

  const handleDiscard = (partial = true) => {
    if (!partial) {
      return setExploration(removeExplorationConversation(currentExploration, cell.conversationId));
    }

    const conversationCells = getConversationCells(
      currentExploration.view.cells,
      cell.conversationId,
    );

    // User requested edits & discards immediately
    if (pendingFirstInput) {
      const cellToEdit = conversationCells.at(0);
      return setExploration(removeConversationEditFlow(currentExploration, cell.id, cellToEdit));
    }

    // Throw away the conversation if user discarded their initial question
    const chatCells = conversationCells.filter(isChatCell);
    if (
      chatCells.length === 1 &&
      conversationCells.length === 1 &&
      chatCells[0].messages.length === 1
    ) {
      if (!isChatCell(conversationCells[0])) {
        return setExploration(
          removeConversationEditFlow(currentExploration, cell.id, conversationCells[0]),
        );
      }

      setConversationDraft('new', messages.at(0)?.message ?? null);
      setNewCellIndex(cellIndex);
      return setExploration(removeExplorationConversation(currentExploration, cell.conversationId));
    }

    setConversationDraft(cell.conversationId, null);
    setExploration(
      setExplorationCells(
        currentExploration,
        removeLastChatInput(currentExploration.view.cells, cell.conversationId, cell),
      ),
    );
  };

  const handleMessageChange = (message: ChatUserMessage) => {
    // Include in-memory messages of this cell in cells list
    const newCells = currentExploration.view.cells.map((c) => {
      if (cell.id === c.id && isChatCell(c)) {
        return {
          ...c,
          messages,
        };
      }

      return c;
    });

    const newExploration = setExplorationCells(
      currentExploration,
      updateConversationMessage(
        cell.conversationId,
        discardConversationAfterMessage(cell.conversationId, newCells, message.id),
        message,
      ),
    );
    setExploration(newExploration);
    const updatedCell = newExploration.view.cells
      .filter((c) => isChatCell(c))
      .find((c) => c.id === cell.id);

    if (updatedCell === undefined) {
      throw new Error('Message editing caused cell to be removed');
    }

    setMessages(updatedCell.messages);
    handleInput(updatedCell.messages);
  };

  // Get all user messages from the conversation for source defaulting
  const allUserMessages = currentExploration.view.cells
    .filter((c): c is ChatCell => isChatCell(c) && c.conversationId === cell.conversationId)
    .flatMap((c) => c.messages)
    .filter(isUserQuestionMessage);

  return (
    <ChatMessages
      messages={messages}
      allUserMessages={allUserMessages}
      status={status}
      statusMessage={statusMessage}
      pendingFirstInput={pendingFirstInput}
      editableInitialMessage={editableInitialMessage}
      draft={conversations[cell.conversationId]?.draft ?? undefined}
      disabled
      onSubmit={handleSubmit}
      onAccept={handleAccept}
      onDiscard={handleDiscard}
      onCancel={handleCancel}
      onAbort={handleAbort}
      onRetry={handleRetry}
      onMessageChange={handleMessageChange}
    />
  );
};
